import { Badge, Button, Collapse } from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import { formatCurrency } from '@vrplatform/utils';
import {
  Box,
  Center,
  Group,
  HoverCard,
  Stack,
  Text,
  Title,
  rem,
  useMantineTheme,
} from '@mantine/core';
import { useCallback, useMemo, useState } from 'react';
import { DrawerCollapsableTable } from '../../_components';
import {
  useReservationAdjustmentMutation,
  useReservationFinancialsModals,
} from '../_hooks';
import type { Reservation } from '../_queries/useLedgerReservationDetailDrawerQuery';
import { ReservationFinancialsAddModal } from './ReservationFinancialsAddModal';
import { ReservationFinancialsDeleteModal } from './ReservationFinancialsDeleteModal';
import { ReservationFinancialsReclassifyModal } from './ReservationFinancialsReclassifyModal';
import {
  getIsReservationFinancialUnmapped,
  useFinancialsSectionColumns,
} from './useReservationFinancialsSectionColumns';
import { LazyTable } from '@vrplatform/table';
import { ReservationSectionJumpTo } from './ReservationSectionJumpTo';
import { RESERVATION_ENTITY_SECTION_ROW_LIMIT } from '../_utils/reservation-entity-section-row-limit';
import { useLineMappingsQuery } from '@vrplatform/data';

type Props = {
  reservation: {
    id: string;
    status: Reservation['status'];
    bookingPlatform?: Reservation['bookingPlatform'];
    lock?: Reservation['lock'];
    bookedAt?: Reservation['bookedAt'];
    checkIn?: Reservation['checkIn'];
    checkOut?: Reservation['checkOut'];
    currency: Reservation['currency'];
    financials: Reservation['financials'];
    totals: Reservation['totals'];
    payment: Reservation['payment'];
    lines: Reservation['lines'];
    isManualReservation: Reservation['isManualReservation'];
    isCancelledAndPending: Reservation['isCancelledAndPending'];
  };
  type: 'tab' | 'collapse';
  goToTab: () => void;
};

const Empty = () => (
  <Center>
    <Text size="sm" c="gray">
      No financials available
    </Text>
  </Center>
);

export const ReservationFinancialsSection = ({
  reservation,
  type,
  goToTab,
}: Props) => {
  const { edit, remove, reclassify } = useReservationFinancialsModals();

  const theme = useMantineTheme();

  const columns = useFinancialsSectionColumns({
    reservation,
    openDeleteAdjustment: remove.setOpened,
    openEditAdjustment: edit.setOpened,
    openReclassifyLine: reclassify.setOpened,
  });

  const addButtonType = useMemo<'financial' | 'adjustment'>(() => {
    // Synced reservation always add adjustments
    if (!reservation.isManualReservation) return 'adjustment';

    // Locked manual reservation add adjustments
    if (reservation.lock?.status === 'locked') return 'adjustment';

    // Otherwise add financials
    return 'financial';
  }, [reservation.isManualReservation, reservation.lock?.status]);

  const rightSection = (
    <Button
      variant="light"
      size="xs"
      leftIcon="PlusIcon"
      color="neutral"
      iconColor={theme.colors.neutral[5]}
      onClick={() =>
        edit.setOpened({
          centTotal: undefined,
          description: '',
          lineType: null!,
          postingAt: null,
          reservationId: reservation.id,
          party: 'owners',
          type: addButtonType,
        })
      }
    >
      {addButtonType === 'adjustment' ? 'Add adjustment' : 'Add financial'}
    </Button>
  );

  const title = 'Financials';

  if (
    type === 'collapse' &&
    reservation.financials.length > RESERVATION_ENTITY_SECTION_ROW_LIMIT
  )
    return (
      <ReservationSectionJumpTo
        title={title}
        count={
          <CustomCount
            lines={reservation.lines}
            currency={reservation.currency}
          />
        }
        goToTab={goToTab}
      />
    );

  return (
    <>
      <ReservationFinancialsAddModal
        closeModal={() => edit.setOpened(null)}
        reservation={{
          id: reservation.id,
          status: reservation.status,
          bookingPlatform: reservation.bookingPlatform,
          lock: reservation.lock,
          bookedAt: reservation.bookedAt,
          checkIn: reservation.checkIn,
          checkOut: reservation.checkOut,
        }}
        reservationLine={edit.opened}
      />
      <ReservationFinancialsDeleteModal
        closeModal={() => remove.setOpened(null)}
        reservationLine={remove.opened}
        reservationId={reservation.id}
      />

      <ReservationFinancialsReclassifyModal
        closeModal={() => reclassify.setOpened(null)}
        reservationLine={reclassify.opened}
        reservation={{
          id: reservation.id,
          bookingPlatform: reservation.bookingPlatform,
          currency: reservation.currency,
          bookedAt: reservation.bookedAt,
          checkIn: reservation.checkIn,
          checkOut: reservation.checkOut,
        }}
      />

      {reservation.isCancelledAndPending && (
        <CancellationPendingBanner
          reservationId={reservation.id}
          bookingPlatform={reservation.bookingPlatform}
          currency={reservation.currency}
          financials={reservation.financials}
          payment={reservation.payment}
          openEditAdjustment={edit.setOpened}
        />
      )}

      {type === 'tab' ? (
        <>
          <Collapse
            title={
              <Text component="span" size="sm" fw={500}>
                {title}
                <Text c={'gray'} ml="xs" size="xs" component="span">
                  <CustomCount
                    lines={reservation.lines}
                    currency={reservation.currency}
                  />
                </Text>
              </Text>
            }
            rightSection={rightSection}
            hideToggle
          />
          <LazyTable
            data={{
              rows: reservation.financials,
              error: null,
              loading: false,
              rowCount: reservation.financials.length,
            }}
            table={{
              columns,
              hideTopBar: true,
              hideHeader: true,
              enableStickyFooter: true,
            }}
          />
        </>
      ) : (
        <DrawerCollapsableTable
          title={title}
          customRowCount={
            <CustomCount
              lines={reservation.lines}
              currency={reservation.currency}
            />
          }
          rightSection={rightSection}
          rowData={reservation.financials}
          columns={columns}
          emptyRowsFallback={Empty}
          styles={{
            row: ({ row }, theme) => {
              const isUnmapped = getIsReservationFinancialUnmapped(
                row.original
              );

              return {
                backgroundColor: isUnmapped
                  ? `${theme.colors.gray[1]}!important`
                  : undefined,
                '.row-action-icon': {
                  opacity: 0,
                  transition: 'opacity 0.2s ease-in-out',
                },
                '&:hover .row-action-icon': {
                  opacity: 1,
                },
              };
            },
          }}
        />
      )}
    </>
  );
};

const CustomCount = ({
  currency,
  lines,
}: {
  lines: Reservation['lines'];
  currency: Reservation['currency'];
}) => {
  const { excluded, included } = useMemo(() => {
    return {
      excluded: lines.filter((line) => line.isExcluded),
      included: lines.filter((line) => !line.isExcluded),
    };
  }, [lines]);

  // if (!excluded.length) return null;

  return (
    <HoverCard withArrow shadow="md">
      <HoverCard.Target>
        <Text span size="xs">
          {included.length}
          {excluded.length ? ` (${excluded.length})` : ''}
        </Text>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack gap="xs">
          {!!included.length && (
            <>
              <Text fw={500} flex={1}>
                Included financials
              </Text>
              {included.map((line) => (
                <Group key={line.id} wrap="nowrap" align="flex-start">
                  <Text c="gray" flex={1}>
                    {line.description}
                  </Text>
                  <Text ta="right" fw={500}>
                    {formatCurrency((line.amount ?? 0) / 100, currency)}
                  </Text>
                </Group>
              ))}
            </>
          )}
          {!!excluded.length && (
            <>
              <Text fw={500} flex={1}>
                Excluded financials
              </Text>
              {excluded.map((line) => (
                <Group key={line.id} wrap="nowrap" align="flex-start">
                  <Text c="gray" flex={1}>
                    {line.description}
                  </Text>
                  <Text ta="right" fw={500}>
                    {formatCurrency((line.amount ?? 0) / 100, currency)}
                  </Text>
                </Group>
              ))}
            </>
          )}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

const CancellationPendingBanner = ({
  payment,
  currency,
  financials,
  reservationId,
  bookingPlatform,
  openEditAdjustment,
}: {
  payment: Reservation['payment'];
  currency: Reservation['currency'];
  financials: Reservation['financials'];
  reservationId: string;
  bookingPlatform?: Reservation['bookingPlatform'];
  openEditAdjustment: ReturnType<
    typeof useReservationFinancialsModals
  >['edit']['setOpened'];
}) => {
  const theme = useMantineTheme();

  const [loadingAutobalance, setLoadingAutobalance] = useState(false);

  const { loading: loadingRestore, submit: submitRestore } =
    useReservationAdjustmentMutation(reservationId, () => {});

  const cancellationLineMappingsQuery = useLineMappingsQuery({
    type: 'reservation',
    search: 'cancellation',
  });

  const restore = () =>
    submitRestore('adjustment', {
      type: 'add',
      data: financials
        .filter(
          (line) => line.type === 'financial' && line.status === 'inactive'
        )
        .map((line) => ({
          description: line.description,
          // biome-ignore lint/suspicious/noNonNullAssertedOptionalChain: any
          lineType: line.reservationLine?.type!,
          party: 'owners',
          centTotal: line.centTotal,
          type: 'adjustment',
          postingAt: undefined,
        })),
    });

  const autobalance = useCallback(async () => {
    setLoadingAutobalance(true);

    try {
      const lineMappings =
        cancellationLineMappingsQuery.data ??
        (await cancellationLineMappingsQuery.refetch()).data ??
        [];

      const cancellationLineType =
        lineMappings.find(
          (lineMapping) =>
            lineMapping.type.toLowerCase().includes('cancellation') &&
            (bookingPlatform
              ? (lineMapping.channelMappings.find(
                  (channelMapping) =>
                    channelMapping.bookingChannel === bookingPlatform
                )?.status ?? lineMapping.status) === 'mapped'
              : lineMapping.status === 'mapped')
        )?.type ?? '';

      openEditAdjustment({
        centTotal: payment.received - payment.expected,
        party: 'owners',
        description: 'Cancellation revenue',
        postingAt: null,
        reservationId,
        lineType: cancellationLineType,
        type: 'adjustment',
      });
    } finally {
      setLoadingAutobalance(false);
    }
  }, [
    cancellationLineMappingsQuery,
    bookingPlatform,
    reservationId,
    openEditAdjustment,
    payment.expected,
    payment.received,
  ]);

  return (
    <Box
      sx={(theme) => ({
        boxShadow: theme.shadows.md,
        border: '1px solid',
        borderColor: theme.colors.yellow[5],
        backgroundColor: theme.colors.yellow[0],
        padding: `${theme.spacing.sm} ${theme.spacing.sm}`,
        borderRadius: theme.radius.md,
      })}
    >
      <Title order={5} fw={500} mb={rem(5)} c={theme.colors.neutral[7]}>
        Reservation Cancelled - Action Required
      </Title>

      <Text mb="md" size="xs" c="neutral">
        This reservation was canceled, leaving a balance of{' '}
        {formatCurrency(payment.received / 100, currency)} from previous
        payments.
      </Text>

      <Group mb={rem(8)} gap="xs" justify="flex-start">
        <Icon icon="CalendarCrossIcon" size={16} />
        <Text size="sm" c={theme.colors.neutral[7]}>
          Cancelled Reservation:
        </Text>
        <Badge
          color="teal"
          size="md"
          sx={{
            marginLeft: 'auto',
          }}
        >
          {formatCurrency(payment.expected / 100, currency)}
        </Badge>
      </Group>

      <Group mb="md" gap="xs" justify="flex-start">
        <Icon icon="CreditCardIncomeIcon" size={16} />
        <Text size="sm" c={theme.colors.neutral[7]}>
          Current Payments Total:
        </Text>
        <Badge
          color="blue"
          size="md"
          sx={{
            marginLeft: 'auto',
          }}
        >
          {formatCurrency(payment.received / 100, currency)}
        </Badge>
      </Group>

      <Group justify="end">
        <Button
          size="xs"
          onClick={restore}
          disabled={loadingAutobalance}
          loading={loadingRestore}
        >
          Restore financials
        </Button>
        <Button
          size="xs"
          variant="primary"
          onClick={autobalance}
          disabled={loadingRestore}
          loading={loadingAutobalance}
        >
          Add{' '}
          {formatCurrency(
            (payment.received - payment.expected) / 100,
            currency
          )}{' '}
          adjustment
        </Button>
      </Group>
    </Box>
  );
};
